import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { regionalInPersonAPI, taxonomyAPI, preferencesAPI } from '../services/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Plus, Search, MoreHorizontal, Eye, Edit2, Trash2, Copy, Tent, MapPin, Calendar,
  Users, Star, Globe, BarChart3, Filter, CheckCircle2, XCircle,
  Upload, Download, FileSpreadsheet, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ProjectStatusCalendar } from '../components/private-deliveries/ProjectStatusCalendar';
import { ColumnsPicker } from '../components/shared/ColumnsPicker';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';

const DELIVERY_STATUS_STYLES = {
  Scheduled:           { bg: 'bg-blue-100 text-blue-800' },
  Delivered:           { bg: 'bg-green-100 text-green-800' },
  Cancelled:           { bg: 'bg-red-100 text-red-800' },
  Postponed:           { bg: 'bg-yellow-100 text-yellow-800' },
  'Converted to Virtual': { bg: 'bg-purple-100 text-purple-800' },
};
const REGION_COLORS = { Asia: '#84cc16', Americas: '#3b82f6', EMEA: '#f59e0b', MEA: '#14b8a6', Other: '#94a3b8' };
const WORKLOAD_COLORS = ['#84cc16', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316'];

// RIO events have no sub-sessions — each event is its own calendar entry, colored by its own delivery_status
const RIO_CALENDAR_COLORS = {
  Delivered: { chip: 'bg-green-500', label: 'Delivered' },
  Cancelled: { chip: 'bg-red-500', label: 'Cancelled' },
  Scheduled: { chip: 'bg-sky-500', label: 'Scheduled' },
  Postponed: { chip: 'bg-orange-500', label: 'Postponed' },
  'Converted to Virtual': { chip: 'bg-purple-500', label: 'Converted to Virtual' },
};

const fmt = (d) => { try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d || '—'; } };

export const RegionalInPersonPage = () => {
  const { user, isPM } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [colFilters, setColFilters] = useState({ region: new Set(), status: new Set() });
  const setColFilter = (key, s) => setColFilters(prev => ({ ...prev, [key]: s }));
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [page, setPage] = useState(1);
  const importFileRef = useRef();
  const [categories, setCategories] = useState([]);
  const [visibleCustomCols, setVisibleCustomCols] = useState([]);
  const customColumns = categories.map(c => ({ key: `custom:${c.id}`, taxonomyKey: c.id, label: c.label }));

  useEffect(() => {
    taxonomyAPI.getAll().then(setCategories).catch(() => {});
    preferencesAPI.get().then(prefs => {
      if (Array.isArray(prefs.regional_in_person_columns)) setVisibleCustomCols(prefs.regional_in_person_columns);
    }).catch(() => {});
  }, []);
  const toggleCustomCol = (key) => {
    setVisibleCustomCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      preferencesAPI.set('regional_in_person_columns', next).catch(() => {});
      return next;
    });
  };

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await regionalInPersonAPI.getAll();
      setEvents(data);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const data = await regionalInPersonAPI.getAnalytics();
      setAnalytics(data);
    } catch { toast.error('Failed to load analytics'); }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { if (tab === 'dashboard') loadAnalytics(); }, [tab, loadAnalytics]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await regionalInPersonAPI.delete(id); toast.success('Event deleted'); loadEvents(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleCopy = async (id) => {
    try {
      const copied = await regionalInPersonAPI.copy(id, user?.id);
      toast.success('Event copied successfully');
      loadEvents();
      navigate(`/regional-in-person/${copied.id}`);
    } catch { toast.error('Failed to copy event'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const result = await regionalInPersonAPI.bulkImport(file, user?.id);
      setImportResult(result);
      if (result.created > 0) { loadEvents(); toast.success(result.message); }
    } catch { toast.error('Import failed — check the file format'); }
    finally { setImportLoading(false); e.target.value = ''; }
  };

  const filtered = events.filter(e => {
    if (search) {
      const s = search.toLowerCase();
      if (!((e.title || '').toLowerCase().includes(s) ||
        (e.city || '').toLowerCase().includes(s) ||
        (e.country || '').toLowerCase().includes(s) ||
        (e.workload || '').toLowerCase().includes(s))) return false;
    }
    if (colFilters.region.size && !colFilters.region.has(e.region)) return false;
    if (colFilters.status.size && !colFilters.status.has(e.delivery_status || e.status)) return false;
    return true;
  });

  const regionOpts = [...new Set(events.map(e => e.region).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const statusOpts = [...new Set(events.map(e => e.delivery_status || e.status).filter(Boolean))].sort().map(v => ({ value: v, label: v }));

  useEffect(() => { setPage(1); }, [search, colFilters]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const kpi = analytics?.kpis || {};

  return (
    <>
    <div className="space-y-6 animate-fade-in" data-testid="rio-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Regional In-Person Events</h1>
          <p className="text-muted-foreground">Small Tent Events across Asia, Americas &amp; EMEA</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => regionalInPersonAPI.downloadTemplate()}
            data-testid="rio-download-template-btn" className="hidden sm:flex">
            <Download className="w-4 h-4 mr-2" />Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
            data-testid="rio-bulk-import-btn" className="hidden sm:flex">
            <Upload className="w-4 h-4 mr-2" />Bulk Import
          </Button>
          <Button onClick={() => navigate('/regional-in-person/new')} data-testid="new-rio-btn"
            className="bg-lime text-lime-foreground hover:bg-lime/90">
            <Plus className="w-4 h-4 mr-2" />New Event
          </Button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {[['events', Tent, 'Events'], ['dashboard', BarChart3, 'Dashboard'], ['calendar', Calendar, 'Calendar']].map(([key, Icon, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'events' && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="pt-5">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search city, country, workload..." value={search}
                    onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="rio-search" />
                </div>
                {customColumns.length > 0 && (
                  <ColumnsPicker columns={customColumns} visibleKeys={visibleCustomCols} onToggle={toggleCustomCol} testId="rio-columns" />
                )}
              </div>
            </CardContent>
          </Card>

          <ActiveFiltersBar
            filters={[
              { key: 'region', label: 'Region', set: colFilters.region, options: regionOpts, onChange: (s) => setColFilter('region', s) },
              { key: 'status', label: 'Status', set: colFilters.status, options: statusOpts, onChange: (s) => setColFilter('status', s) },
            ]}
            search={search}
            onClearSearch={() => setSearch('')}
            testId="rio-active-filters"
          />

          {/* Table */}
          <Card>
            <CardContent className="pt-5">
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
              ) : filtered.length > 0 ? (
                <Table containerClassName="max-h-[calc(100vh-320px)]">
                  <TableHeader sticky>
                    <TableRow>
                      <TableHead>Event / Course</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead><ColumnFilterPopover label="Region" options={regionOpts} selected={colFilters.region} onChange={(s) => setColFilter('region', s)} testId="rio-col-filter-region" /></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Trainers</TableHead>
                      <TableHead>Attendance</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead><ColumnFilterPopover label="Status" options={statusOpts} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} testId="rio-col-filter-status" /></TableHead>
                      {customColumns.filter(c => visibleCustomCols.includes(c.key)).map(c => (
                        <TableHead key={c.key}>{c.label}</TableHead>
                      ))}
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.map(e => (
                      <TableRow key={e.id} className="cursor-pointer hover:bg-muted/50"
                        data-testid={`rio-row-${e.id}`}
                        onClick={() => navigate(`/regional-in-person/${e.id}`)}>
                        <TableCell>
                          <div className="font-medium text-sm leading-tight max-w-[220px] truncate">{e.title}</div>
                          {e.course_type && <Badge variant="outline" className="text-xs mt-0.5">{e.course_type}</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                            {[e.city, e.country].filter(Boolean).join(', ') || '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {e.region ? (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                              style={{ background: REGION_COLORS[e.region] + '22', color: REGION_COLORS[e.region] }}>
                              {e.region}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {e.preferred_dates ? fmt(e.preferred_dates) : '—'}
                        </TableCell>
                        <TableCell className="text-center text-sm">{e.delivery_days ?? '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            {e.trainer1_name && <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{e.trainer1_name.split(' ')[0]}</span>}
                            {e.trainer2_name && <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{e.trainer2_name.split(' ')[0]}</span>}
                            {!e.trainer1_name && !e.trainer2_name && <span className="text-muted-foreground text-xs">TBD</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {e.total_attendance != null ? (
                            <span className="flex items-center gap-1"><Users className="w-3 h-3 text-muted-foreground" />{e.total_attendance}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {e.feedback_score != null ? (
                            <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />{e.feedback_score}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge className={DELIVERY_STATUS_STYLES[e.delivery_status]?.bg || 'bg-gray-100 text-gray-700'}>
                            {e.delivery_status || 'Scheduled'}
                          </Badge>
                        </TableCell>
                        {customColumns.filter(col => visibleCustomCols.includes(col.key)).map(col => (
                          <TableCell key={col.key} className="text-sm">{e.custom_fields?.[col.taxonomyKey] || '—'}</TableCell>
                        ))}
                        <TableCell onClick={ev => ev.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/regional-in-person/${e.id}`)}>
                                <Eye className="w-4 h-4 mr-2" />View
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/regional-in-person/${e.id}/edit`)}>
                                <Edit2 className="w-4 h-4 mr-2" />Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCopy(e.id)} data-testid={`rio-copy-${e.id}`}>
                                <Copy className="w-4 h-4 mr-2" />Copy Event
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(e.id)} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-16">
                  <Tent className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No events found</h3>
                  <p className="text-muted-foreground mb-4">{search || statusFilter !== 'all' ? 'Try adjusting filters' : 'Create your first Regional In-Person event'}</p>
                  {!search && statusFilter === 'all' && (
                    <Button onClick={() => navigate('/regional-in-person/new')} className="bg-lime text-lime-foreground hover:bg-lime/90">
                      <Plus className="w-4 h-4 mr-2" />New Event
                    </Button>
                  )}
                </div>
              )}
              <TablePagination page={page} total={filtered.length} onPageChange={setPage} testId="rio-pagination" itemLabel="events" />
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'dashboard' && (
        <div className="space-y-6">
          {!analytics ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-28 bg-muted animate-pulse rounded-lg" />)}
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: 'Total Learners', value: kpi.total_learners?.toLocaleString() ?? '0', icon: Users, color: 'text-lime' },
                  { label: 'Unique Partners', value: kpi.unique_partners?.toLocaleString() ?? '0', icon: Globe, color: 'text-blue-500' },
                  { label: 'Sessions Delivered', value: kpi.sessions_conducted ?? '0', icon: CheckCircle2, color: 'text-green-500' },
                  { label: 'English Sessions', value: kpi.english_sessions ?? '0', icon: Globe, color: 'text-slate-500' },
                  { label: 'Regional Language', value: kpi.regional_lang_sessions ?? '0', icon: Globe, color: 'text-orange-500' },
                  { label: 'Avg per Session', value: kpi.avg_learners_per_session ?? '0', icon: Users, color: 'text-purple-500' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <Card key={label} className="border border-border/50">
                    <CardContent className="py-4 px-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <Icon className={`w-4 h-4 ${color}`} />
                      </div>
                      <p className="text-2xl font-bold">{value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Latest Feedback */}
              {kpi.latest_feedback && (
                <Card className="border-l-4 border-lime bg-lime/5">
                  <CardContent className="py-3 px-5">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Latest Trainer Feedback</p>
                    <p className="text-sm italic text-foreground">"{kpi.latest_feedback}"</p>
                  </CardContent>
                </Card>
              )}

              {/* Charts row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Region chart */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Sessions by Region</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.region_chart} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="region" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="sessions" radius={[4,4,0,0]}>
                          {(analytics.region_chart || []).map((entry, idx) => (
                            <Cell key={entry.region} fill={REGION_COLORS[entry.region] || '#84cc16'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Workload chart */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Sessions by Workload</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.workload_chart} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="workload" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="sessions" radius={[4,4,0,0]}>
                          {(analytics.workload_chart || []).map((entry, idx) => (
                            <Cell key={entry.workload} fill={WORKLOAD_COLORS[idx % WORKLOAD_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Feedback vs City chart */}
              {analytics.city_feedback_chart?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Avg Feedback Score by City/Workload</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={analytics.city_feedback_chart} margin={{ top: 4, right: 4, bottom: 60, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" />
                        <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [`${v}/5`, 'Avg Score']} />
                        <Bar dataKey="avg_score" fill="#f59e0b" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Region + Workload tables */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Region-wise Summary</CardTitle></CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Region</TableHead>
                          <TableHead className="text-xs text-right">Sessions</TableHead>
                          <TableHead className="text-xs text-right">Learners</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(analytics.region_table || []).map(r => (
                          <TableRow key={r.region}>
                            <TableCell className="text-sm font-medium">{r.region}</TableCell>
                            <TableCell className="text-sm text-right">{r.sessions}</TableCell>
                            <TableCell className="text-sm text-right">{r.learners.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Solution Area Summary</CardTitle></CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Workload</TableHead>
                          <TableHead className="text-xs text-right">Sessions</TableHead>
                          <TableHead className="text-xs text-right">Learners</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(analytics.workload_table || []).map(w => (
                          <TableRow key={w.workload}>
                            <TableCell className="text-sm font-medium">{w.workload}</TableCell>
                            <TableCell className="text-sm text-right">{w.sessions}</TableCell>
                            <TableCell className="text-sm text-right">{w.learners.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'calendar' && (
        <ProjectStatusCalendar
          items={events.map(e => ({
            id: e.id,
            date: e.preferred_dates,
            endDate: e.training_end_date,
            title: e.title,
            trainers: [e.trainer1_name, e.trainer2_name].filter(Boolean),
            pm: e.pm_name,
            statusKey: e.delivery_status || 'Scheduled',
          }))}
          statusColors={RIO_CALENDAR_COLORS}
          onItemClick={(it) => navigate(`/regional-in-person/${it.id}`)}
          testId="rio-calendar-view"
        />
      )}
    </div>  {/* closes outer conditional wrapper */}

    {/* ── Bulk Import Dialog ─────────────────────────────────── */}
    <input ref={importFileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
      onChange={handleImport} data-testid="rio-import-file-input" />
    <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-lime" />Bulk Import RIO Events
          </DialogTitle>
          <DialogDescription>
            Upload an Excel (.xlsx) file with multiple events. Download the template to see the required format.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <button onClick={() => regionalInPersonAPI.downloadTemplate()}
            className="w-full flex items-center justify-center gap-2 border border-dashed border-lime/60 text-lime text-sm rounded-lg py-3 hover:bg-lime/5 transition-colors"
            data-testid="rio-import-download-template">
            <Download className="w-4 h-4" />Download Template
          </button>
          <button onClick={() => importFileRef.current?.click()}
            disabled={importLoading}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-border text-muted-foreground text-sm rounded-lg py-6 hover:border-lime/40 hover:text-foreground transition-colors disabled:opacity-50"
            data-testid="rio-import-upload-btn">
            {importLoading
              ? <><span className="animate-spin w-4 h-4 border-2 border-lime border-t-transparent rounded-full inline-block" />Processing...</>
              : <><Upload className="w-5 h-5" />Click to upload XLSX file</>
            }
          </button>
          {importResult && (
            <div className={`rounded-lg p-3 text-sm ${importResult.created > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className="font-medium flex items-center gap-2">
                {importResult.created > 0
                  ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                  : <AlertCircle className="w-4 h-4 text-red-600" />}
                {importResult.message}
              </p>
              {importResult.errors?.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-red-700">
                  {importResult.errors.map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RegionalInPersonPage;
